/**
 * 日付ページングの共有フック
 *
 * 無限スクロールのタイムライン／カレンダーで、
 * 「今日から過去へ7日ずつ、最大60日分」の読み込みを共通化する。
 * 取得結果は react-query のキャッシュに載るため、表示を切り替えても取り直さない。
 */

import { useQueries, type UseQueryResult } from '@tanstack/react-query'
import { differenceInCalendarDays, format, subDays } from 'date-fns'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'

import type { GarminActivitySummary } from '@/api/types/garmin'
import { useAppStore } from '@/store/useAppStore'
import { useGarminStore } from '@/store/useGarminStore'

import { type DayData, dayDataQueryKey, fetchDayData } from './day-data'
import { useDayVisitSync } from './use-day-visit-sync'

/** 初期表示日数と、追加読み込み1回あたりの日数 */
const DAYS_PER_PAGE = 7
/** さかのぼれる最大日数 */
export const MAX_LOADED_DAYS = 60

/** 日別データに、一括取得した Garmin アクティビティを日付で振り分けたもの */
export type ActivityDayData = DayData & { activities: GarminActivitySummary[] }

/** from から過去へ count 日分の日付を新しい順で返す */
const pastDates = (from: Date, count: number): string[] =>
  Array.from({ length: count }, (_, i) => format(subDays(from, i), 'yyyy-MM-dd'))

/**
 * @param targetDate ヘッダなど外から指定される表示対象日(YYYY-MM-DD)。変わるとその日まで自動でジャンプする
 */
export const useActivityDayPaging = (targetDate?: string) => {
  const hasFullAccess = useAppStore((state) => state.hasFullAccess)
  const { activities, fetchActivities } = useGarminStore()

  // 新しい順に保持する（先頭が今日）
  const [loadedDates, setLoadedDates] = useState<string[]>(() => pastDates(new Date(), DAYS_PER_PAGE))
  // ジャンプ先。各ビューが自分のやり方でスクロールしたら clearPendingJump で消す
  const [pendingJumpDate, setPendingJumpDate] = useState<string | null>(null)
  const clearPendingJump = useCallback(() => setPendingJumpDate(null), [])

  // シート経由で更新・削除された Visit をキャッシュに反映
  useDayVisitSync()

  useEffect(() => {
    if (hasFullAccess) {
      void fetchActivities(100)
    }
  }, [hasFullAccess, fetchActivities])

  const activitiesByDate = useMemo(() => {
    const map = new Map<string, GarminActivitySummary[]>()
    for (const activity of activities) {
      const key = format(new Date(activity.start_time), 'yyyy-MM-dd')
      const list = map.get(key)
      if (list) {
        list.push(activity)
        continue
      }
      map.set(key, [activity])
    }
    return map
  }, [activities])

  // useQueries は combine の結果をメモ化するため、combine 自体も安定させておく
  const combine = useCallback(
    (results: UseQueryResult<DayData>[]) => {
      const dayDataMap = new Map<string, ActivityDayData>()
      for (const { data } of results) {
        if (!data) continue
        dayDataMap.set(data.date, { ...data, activities: activitiesByDate.get(data.date) ?? [] })
      }
      return { dayDataMap, isLoadingMore: results.some((result) => result.isPending) }
    },
    [activitiesByDate],
  )

  const { dayDataMap, isLoadingMore } = useQueries({
    queries: loadedDates.map((dateStr) => ({
      queryKey: dayDataQueryKey(dateStr, hasFullAccess),
      queryFn: () => fetchDayData(dateStr, { hasFullAccess }),
    })),
    combine,
  })

  // 過去のデータをさらに読み込む（上スクロール時）
  const loadMorePast = useCallback(() => {
    if (isLoadingMore) return
    setLoadedDates((prev) => {
      const oldestDate = prev.at(-1)
      // 上限に達したら、取得しても捨てるだけなので打ち切る
      if (!oldestDate || prev.length >= MAX_LOADED_DAYS) return prev
      const olderDates = pastDates(subDays(new Date(`${oldestDate}T00:00:00`), 1), DAYS_PER_PAGE)
      return [...prev, ...olderDates].slice(0, MAX_LOADED_DAYS)
    })
  }, [isLoadingMore])

  // 指定日まで一気に移動する（その日から今日までを読み込み対象に加える）
  const jump = useCallback((dateStr: string) => {
    const today = new Date()
    const diffDays = differenceInCalendarDays(today, new Date(`${dateStr}T00:00:00`))
    if (diffDays < 0) return

    const dates = pastDates(today, Math.min(diffDays, MAX_LOADED_DAYS - 1) + 1)
    // 既にロード済みの古い日も残し、新しい順で上限まで保持する
    setLoadedDates((prev) =>
      Array.from(new Set([...dates, ...prev]))
        .sort((a, b) => b.localeCompare(a))
        .slice(0, MAX_LOADED_DAYS),
    )
    setPendingJumpDate(dates.at(-1) ?? dateStr)

    if (diffDays > MAX_LOADED_DAYS - 1) {
      toast.info(`直近${MAX_LOADED_DAYS}日分まで表示しました。それより前は上にスクロールしてください`)
    }
  }, [])

  // 初期表示は今日を基準にするので、今日以外が指定されたときだけ動かす
  const [lastTarget, setLastTarget] = useState(() => format(new Date(), 'yyyy-MM-dd'))
  useEffect(() => {
    if (!targetDate || targetDate === lastTarget) return
    setLastTarget(targetDate)
    jump(targetDate)
  }, [targetDate, lastTarget, jump])

  return {
    /** 日付(YYYY-MM-DD) -> その日のデータ */
    dayDataMap,
    /** 新しい順の読み込み済み日付 */
    loadedDates,
    isLoadingMore,
    /** ジャンプ先の取得を待っているか */
    isJumping: pendingJumpDate !== null && isLoadingMore,
    loadMorePast,
    /** 指定日を読み込み対象に加えて pendingJumpDate に反映する */
    jump,
    /** スクロール先の日付。スクロールしたら clearPendingJump で消す */
    pendingJumpDate,
    clearPendingJump,
  }
}
